import JustValidate, { Rules } from 'just-validate';
import { i18n } from './i18n.js';

// Téléphone français ou international : 8 à 13 chiffres, espaces / points / tirets tolérés, +33 ou 0033 en préfixe
const PHONE = /^(?:(?:\+|00)\d{1,3}[\s.-]?)?(?:\(0\)[\s.-]?)?\d(?:[\s.-]?\d){7,12}$/;

/**
 * Validation du formulaire de demande d'informations (just-validate).
 * Marqueur : data-info-form sur le <form>. Les champs sont repérés par leur attribut name.
 */
export class InfoForm {
  constructor(root) {
    this.root = root;
    this.validator = new JustValidate(root, {
      errorFieldCssClass: 'form__field--error',
      errorLabelCssClass: 'form__error',
      errorLabelStyle: undefined, // le style des messages est dans _cta.scss
      focusInvalidField: true,
      lockForm: true,
      // Pour l'instant, soumission native (action="#"). À remplacer par un fetch() vers le service d'envoi
      // en passant submitFormAutomatically à false et en postant dans onSuccess.
      submitFormAutomatically: true,
    });
    this.status = this.root.querySelector('[data-form-status]');
    // à chaque validation : aria-invalid suit l'état de chaque champ (lecteurs d'écran)
    this.validator.onValidate(({ fields }) => {
      Object.values(fields).forEach(({ elem, isValid }) => {
        elem.setAttribute('aria-invalid', String(!isValid));
      });
    });
    // à l'échec de l'envoi : région aria-live qui annonce les erreurs (le focus va aussi au 1er champ invalide)
    this.validator.onFail((fields) => {
      const messages = Object.values(fields)
        .filter((f) => !f.isValid && f.errorMessage)
        .map((f) => f.errorMessage);
      this.announce(`${i18n.t('form.errorsSummary')} ${messages.join(' ')}`);
    });

    this.validator
      .addField('[name="prenom"]', [
        { rule: Rules.Required, errorMessage: () => i18n.t('form.errors.firstnameRequired') },
        { rule: Rules.MinLength, value: 2, errorMessage: () => i18n.t('form.errors.firstnameMin') },
      ])
      .addField('[name="nom"]', [
        { rule: Rules.Required, errorMessage: () => i18n.t('form.errors.lastnameRequired') },
        { rule: Rules.MinLength, value: 2, errorMessage: () => i18n.t('form.errors.lastnameMin') },
      ])
      .addField('[name="email"]', [
        { rule: Rules.Required, errorMessage: () => i18n.t('form.errors.emailRequired') },
        { rule: Rules.Email, errorMessage: () => i18n.t('form.errors.emailInvalid') },
      ])
      .addField('[name="telephone"]', [
        {
          // facultatif : validé seulement s'il est renseigné
          validator: (value) => !value.trim() || PHONE.test(value.trim()),
          errorMessage: () => i18n.t('form.errors.phoneInvalid'),
        },
      ])
      .addField('[name="message"]', [
        { rule: Rules.Required, errorMessage: () => i18n.t('form.errors.messageRequired') },
        { rule: Rules.MinLength, value: 10, errorMessage: () => i18n.t('form.errors.messageMin') },
        { rule: Rules.MaxLength, value: 2000, errorMessage: () => i18n.t('form.errors.messageMax') },
      ])
      .addField(
        '[name="consentement"]',
        [{ rule: Rules.Required, errorMessage: () => i18n.t('form.errors.consentRequired') }],
        { errorsContainer: this.root.querySelector('[data-error-for="consentement"]') },
      );
  }

  // vide puis remplit la région live : le message est ré-annoncé même s'il est identique au précédent
  announce(message) {
    if (!this.status) return;
    this.status.textContent = '';
    setTimeout(() => {
      this.status.textContent = message;
    }, 50);
  }

  destroy() {
    this.validator.destroy();
  }
}

export function initForms() {
  document.querySelectorAll('[data-info-form]').forEach((form) => new InfoForm(form));
}
